import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function safeNext(next) {
  if (!next || typeof next !== "string") return null;
  if (!next.startsWith("/") || next.startsWith("//")) return null;
  return next;
}

const LS_REMEMBER_EMAIL_KEY = "bpg.visitor.rememberedEmail";
const LS_REMEMBER_FLAG_KEY = "bpg.visitor.rememberMe";

export default function VisitorLogin() {
  const { loginClient } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  // Spec utilisateur Feb 2026 — en mode maintenance on masque les liens
  // « Retour » et « Créer un compte visiteur » pour ne laisser qu'un
  // chemin propre vers la connexion admin.
  const [maintenance, setMaintenance] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const loc = useLocation();
  const next = safeNext(params.get("next"));

  // Restore previously remembered email on mount
  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem(LS_REMEMBER_EMAIL_KEY);
      const savedFlag = localStorage.getItem(LS_REMEMBER_FLAG_KEY);
      if (savedEmail) setForm((f) => ({ ...f, email: savedEmail }));
      if (savedFlag != null) setRememberMe(savedFlag === "1");
    } catch {}
  }, []);

  // Lis l'état maintenance pour conditionner l'affichage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/system/maintenance`);
        if (!cancelled) setMaintenance(!!data?.enabled);
      } catch {
        if (!cancelled) setMaintenance(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginClient(form.email, form.password);
      try {
        if (rememberMe) {
          localStorage.setItem(LS_REMEMBER_EMAIL_KEY, form.email);
          localStorage.setItem(LS_REMEMBER_FLAG_KEY, "1");
        } else {
          localStorage.removeItem(LS_REMEMBER_EMAIL_KEY);
          localStorage.setItem(LS_REMEMBER_FLAG_KEY, "0");
        }
      } catch {}
      toast.success("Connecté !");
      nav(next || "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-16 max-w-md">
      {!maintenance && (
        <Link to={`/login${loc.search}`} className="text-sm text-subtle hover:text-ink inline-flex items-center gap-1" data-testid="back-chooser-link">← Retour</Link>
      )}
      <p className="overline mb-2 mt-4">Visiteur</p>
      <h1 className="font-display text-4xl font-black tracking-tight text-ink">Connexion visiteur</h1>
      <p className="mt-2 text-subtle">Accédez à votre compte BypassGo.</p>
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="h-12 mt-1.5"
            data-testid="email-input"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="login-password">Mot de passe</Label>
            <Link to="/forgot-password?role=client" className="text-xs text-brand hover:underline font-medium" data-testid="forgot-pwd-link">
              Mot de passe oublié ?
            </Link>
          </div>
          <PasswordInput
            id="login-password"
            name="password"
            required
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="h-12 mt-1.5"
            data-testid="password-input"
          />
        </div>
        <div className="flex items-start gap-3 pt-1">
          <Checkbox
            id="remember-me"
            checked={rememberMe}
            onCheckedChange={(v) => setRememberMe(!!v)}
            className="mt-0.5"
            data-testid="remember-me-checkbox"
          />
          <label htmlFor="remember-me" className="text-sm text-ink leading-snug cursor-pointer">
            <span className="font-medium">Enregistrer mon identifiant</span>
            <span className="block text-xs text-subtle mt-0.5">
              Pour le mot de passe, acceptez la proposition de votre navigateur (Samsung Pass, iCloud Keychain, Google…).
            </span>
          </label>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 bg-brand hover:bg-brand-hover rounded-md text-white font-medium" data-testid="submit-btn">
          {loading ? "…" : "Se connecter"}
        </Button>
      </form>
      {!maintenance && (
        <p className="mt-6 text-sm text-subtle">
          Pas de compte ? <Link to={`/visitor/register${loc.search}`} className="text-brand font-medium">Créer un compte visiteur</Link>
        </p>
      )}
    </div>
  );
}
